// src/components/admin/AdminDashboard.tsx - VERSIÓN PROFESIONAL
import React, { useState, useEffect } from 'react';
import apiElCristo from '../../service/api_elcristo';
import './AdminDashboard.css';
import { di } from '@fullcalendar/core/internal-common';

interface AdminDashboardProps {
  user: any;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  const [stats, setStats] = useState({
    totalSections: 10,
    lastUpdated: new Date().toISOString(),
    activeUsers: 0,
    pendingChanges: 0
  });
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Aquí podrías cargar stats reales del backend
      const users = await apiElCristo.getUsers();
      const usersArray = Array.isArray(users) ? users : Object.values(users);
      
      setStats(prev => ({
        ...prev,
        activeUsers: usersArray.length
      }));
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const quickLinks = [
    { icon: 'fas fa-bars', label: 'Menú & Header', path: '/admin/header', color: '#3b82f6' },
    { icon: 'fas fa-image', label: 'Portada Principal', path: '/admin/hero', color: '#8b5cf6' },
    { icon: 'fas fa-info-circle', label: 'Nosotros', path: '/admin/aboutus', color: '#10b981' },
    { icon: 'fas fa-running', label: 'Actividades', path: '/admin/activities', color: '#f59e0b' },
    { icon: 'fas fa-graduation-cap', label: 'Programas', path: '/admin/programs', color: '#ef4444' },
    { icon: 'fas fa-chalkboard-teacher', label: 'Equipo Docente', path: '/admin/team', color: '#06b6d4' },
    { icon: 'fas fa-newspaper', label: 'Noticias', path: '/admin/news', color: '#ec4899' },
    { icon: 'fas fa-quote-right', label: 'Comunicados', path: '/admin/comunicados', color: '#14b8a6' }
  ];

  const helpResources = [
    {
      icon: 'fas fa-book',
      title: 'Documentación',
      description: 'Guía completa del CMS',
      action: () => setShowDocsModal(true),
      color: '#3b82f6'
    },
    {
      icon: 'fas fa-video',
      title: 'Video Tutoriales',
      description: 'Aprende visualmente',
      action: () => setShowVideoModal(true),
      color: '#ef4444',
      disabled: true
    },
    {
      icon: 'fas fa-life-ring',
      title: 'Contactar Soporte',
      description: 'Ayuda personalizada',
      action: () => window.location.href = 'mailto:soporte@colegioelcristo.cl',
      color: '#10b981'
    }
  ];

  return (
    <div className="admin-dashboard-container">
      {/* Welcome Section */}
      <div className="dashboard-welcome">
        <div className="welcome-content">
          <h1>¡Bienvenido, {user?.name || 'Administrador'}! 👋</h1>
          <p>Panel de administración del sitio web Colegio El Cristo</p>
        </div>
        <div className="welcome-badge">
          <i className="fas fa-crown"></i>
          <span>{user?.role || 'Admin'}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dbeafe' }}>
            <i className="fas fa-layer-group" style={{ color: '#3b82f6' }}></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalSections}</h3>
            <p>Secciones del Sitio</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#d1fae5' }}>
            <i className="fas fa-users" style={{ color: '#10b981' }}></i>
          </div>
          <div className="stat-content">
            <h3>{stats.activeUsers}</h3>
            <p>Usuarios Activos</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>
            <i className="fas fa-clock" style={{ color: '#f59e0b' }}></i>
          </div>
          <div className="stat-content">
            <h3>{new Date(stats.lastUpdated).toLocaleDateString('es-CL')}</h3>
            <p>Última Actualización</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2' }}>
            <i className="fas fa-edit" style={{ color: '#ef4444' }}></i>
          </div>
          <div className="stat-content">
            <h3>{stats.pendingChanges}</h3>
            <p>Cambios Pendientes</p>
          </div>
        </div>
      </div>

      {/* Quick Access */}
      <div className="card">
        <div className="card-header">
          <h3><i className="fas fa-bolt"></i> Acceso Rápido</h3>
          <p>Secciones más utilizadas del sitio</p>
        </div>
        <div className="card-body">
          <div className="quick-links-grid">
            {quickLinks.map((link, index) => (
              <a 
                key={index}
                href={link.path}
                className="quick-link-card"
              >
                <div className="quick-link-icon" style={{ background: link.color }}>
                  <i className={link.icon}></i>
                </div>
                <span>{link.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="card">
        <div className="card-header">
          <h3><i className="fas fa-question-circle"></i> ¿Necesitas Ayuda?</h3>
          <p>Este panel te permite gestionar todo el contenido del sitio web de forma visual. Selecciona una sección en el menú lateral para comenzar a editar.</p>
        </div>
        <div className="card-body">
          <div className="help-resources-grid">
            {helpResources.map((resource, index) => (
              <div 
                key={index}
                className="help-resource-card"
                onClick={resource.action}
              >
                <div className="help-icon" style={{ background: resource.color }}>
                  <i className={resource.icon}></i>
                </div>
                <h4>{resource.title}</h4>
                <p>{resource.description}</p>
                <button className="btn-resource">
                  Acceder <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="card-header">
          <h3><i className="fas fa-history"></i> Actividad Reciente</h3>
        </div>
        <div className="card-body">
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="activity-content">
                <p><strong>{user?.name}</strong> inició sesión</p>
                <span>Hace unos momentos</span>
              </div>
            </div>
            <div className="activity-item">
              <div className="activity-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="activity-content">
                <p>Sistema de sesión activo</p>
                <span>Timeout: 30 minutos de inactividad</span>
              </div>
            </div>
            <div className="activity-item">
              <div className="activity-icon">
                <i className="fas fa-database"></i>
              </div>
              <div className="activity-content">
                <p>Backup automático configurado</p>
                <span>Última copia: {new Date().toLocaleTimeString('es-CL')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documentation Modal */}
      {showDocsModal && (
        <div className="modal-overlay" onClick={() => setShowDocsModal(false)}>
          <div className="modal-dialog modal-docs" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-book"></i> Documentación del CMS</h3>
              <button className="btn-close" onClick={() => setShowDocsModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body modal-docs-body">
              <div className="docs-content">
                <h4>📚 Guía de Uso - CMS El Cristo</h4>
                
                <section className="docs-section">
                  <h5>🚀 Inicio Rápido</h5>
                  <ol>
                    <li><strong>Iniciar Sesión:</strong> Usa tus credenciales para acceder al panel</li>
                    <li><strong>Navegar:</strong> Usa el menú lateral para seleccionar la sección a editar</li>
                    <li><strong>Editar:</strong> Modifica el contenido usando los formularios</li>
                    <li><strong>Guardar:</strong> Haz clic en "Guardar Cambios" para aplicar</li>
                  </ol>
                </section>

                <section className="docs-section">
                  <h5>📝 Secciones Disponibles</h5>
                  <ul>
                    <li><strong>Menú & Header:</strong> Edita el menú de navegación y logo</li>
                    <li><strong>Portada Principal:</strong> Imagen hero y mensaje principal</li>
                    <li><strong>Nosotros:</strong> Información sobre el colegio</li>
                    <li><strong>Actividades:</strong> Actividades para alumnos</li>
                    <li><strong>Programas:</strong> Programas educativos ofrecidos</li>
                    <li><strong>Equipo Docente:</strong> Profesores y personal</li>
                    <li><strong>Noticias:</strong> Comunicados y noticias del colegio</li>
                    <li><strong>Comunicados:</strong> Opiniones de padres y apoderados</li>
                  </ul>
                </section>

                <section className="docs-section">
                  <h5>🖼️ Subir Imágenes</h5>
                  <ol>
                    <li>Haz clic en el botón "Subir" junto al campo de imagen</li>
                    <li>Selecciona una imagen (PNG, JPG, WEBP)</li>
                    <li>Espera a que se complete la subida</li>
                    <li>La URL se agregará automáticamente</li>
                  </ol>
                  <p><strong>Límites:</strong> Máximo 5MB por imagen</p>
                </section>

                <section className="docs-section">
                  <h5>🔄 Reordenar Elementos</h5>
                  <p>Usa los botones ↑ y ↓ en cada tarjeta para cambiar el orden de los elementos (programas, actividades, noticias, etc.)</p>
                </section>

                <section className="docs-section">
                  <h5>👥 Gestión de Usuarios</h5>
                  <ul>
                    <li><strong>Admin:</strong> Acceso total, puede crear usuarios</li>
                    <li><strong>Editor:</strong> Puede editar contenido, sin usuarios</li>
                    <li><strong>Viewer:</strong> Solo lectura</li>
                  </ul>
                </section>

                <section className="docs-section">
                  <h5>🔐 Seguridad</h5>
                  <ul>
                    <li>La sesión expira después de 30 minutos de inactividad</li>
                    <li>Se mostrará una advertencia 30 segundos antes</li>
                    <li>Puedes extender la sesión haciendo clic en "Continuar"</li>
                    <li>Todos los cambios requieren autenticación</li>
                  </ul>
                </section>

                <section className="docs-section">
                  <h5>💡 Consejos</h5>
                  <ul>
                    <li>Usa nombres descriptivos para las imágenes</li>
                    <li>Revisa la vista previa antes de guardar</li>
                    <li>El botón "Recargar" descarta cambios no guardados</li>
                    <li>Usa HTML básico en títulos: <code>&lt;br&gt;</code> para saltos de línea</li>
                  </ul>
                </section>

                <section className="docs-section">
                  <h5>🆘 Soporte</h5>
                  <p>¿Problemas o dudas? Contacta a:</p>
                  <p><strong>Email:</strong> <a href="mailto:soporte@colegioelcristo.cl">soporte@colegioelcristo.cl</a></p>
                  <p><strong>Horario:</strong> Lunes a Viernes, 9:00 - 18:00</p>
                </section>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowDocsModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="modal-overlay" onClick={() => setShowVideoModal(false)}>
          <div className="modal-dialog modal-video" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-video"></i> Video Tutorial - CMS El Cristo</h3>
              <button className="btn-close" onClick={() => setShowVideoModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="video-container">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                  title="Tutorial CMS El Cristo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-info">
                <h4>📺 Tutorial Completo del CMS</h4>
                <p>Aprende a usar todas las funciones del panel de administración en este video tutorial paso a paso.</p>
                <ul>
                  <li>✅ Inicio de sesión y navegación</li>
                  <li>✅ Editar contenido de todas las secciones</li>
                  <li>✅ Subir y gestionar imágenes</li>
                  <li>✅ Crear y administrar usuarios</li>
                  <li>✅ Consejos y mejores prácticas</li>
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowVideoModal(false)}>
                Cerrar
              </button>
              <a 
                href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <i className="fas fa-external-link-alt"></i>
                Ver en YouTube
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;